import PropTracker from "./prop-trackers/prop-tracker.js";

import OutputAssetsThree from "./data-output-generator/output-assets-three.js";
//import OutputObjects2DDom from "./data-output-generator/output-objects2d-dom.js";
import OutputLabelsDom from "./data-output-generator/output-labels-dom.js";
import RegionInfo from "./data-output-generator/glraycaster/region-info.js";

import Data from "./data-output-generator/data.js";
import DataThree from "./data-output-generator/data-three.js";
import Output from "./data-output-generator/output.js";

class DataOutputGenerator {
    constructor(
        { struct: { wrappers: wrapCtx, events: evtCtx }, shallow: { wrappers: shlWrapCtx, events: shlEvtCtx } },
        { tour, tourLabels, session },
        {
            rootObject, camera,
            threeFileArrayBufLoader, threeGltfLoader, threeTexLoader,
            obj2dDomElement, obj2dUrlPrefix, labelsDomElement,
            glraycastBuffer,
        }
    ) {
        this._wrapCtx = wrapCtx;
        this._evtCtx = evtCtx;

        const outputAssetsThree = new OutputAssetsThree({ rootObject, camera, threeFileArrayBufLoader, threeGltfLoader, threeTexLoader });
        //const outputObjects2dDom = new OutputObjects2DDom({ domElement: obj2dDomElement, urlPrefix: obj2dUrlPrefix });
        const outputLabelsDom = new OutputLabelsDom({ domElement: labelsDomElement });

        this._outputAssetsThree = outputAssetsThree;
        //this._outputObjects2dDom = outputObjects2dDom;
        this._outputLabelsDom = outputLabelsDom;

        const state = {
            tour: { data: tour || null, events: tour ? evtCtx.getEvents(tour) : null },
            tourLabels: { data: tourLabels || null, events: tourLabels ? evtCtx.getEvents(tourLabels) : null },
            session: { data: session || null, events: session ? evtCtx.getEvents(session) : null },
        };

        const data = new Data(wrapCtx, evtCtx);
        const dataThree = new DataThree(wrapCtx, evtCtx);
        const output = new Output(wrapCtx, evtCtx, shlWrapCtx, shlEvtCtx, {
            assets: outputAssetsThree.params,
            //objects2d: outputObjects2dDom.params,
            labels: outputLabelsDom.params,
        });

        const glraycastRegionInfo = new RegionInfo(outputAssetsThree.params.glraycastElementIds, glraycastBuffer);
        const glraycastMeshTracker = new PropTracker();
        const objOnScreenPos = wrapCtx.getWrapper({});

        const glraycastMeshKey = new Map();

        glraycastRegionInfo.listenUpdate(() => {
            glraycastMeshTracker.updateIfNeeded(({ props }) => {
                for (const { key, oldValue, newValue } of props.iterateModified()) {
                    const oldMesh = oldValue && oldValue.objects.glraycast;
                    const newMesh = newValue && newValue.objects.glraycast;

                    glraycastMeshKey.delete(oldMesh);
                    glraycastMeshKey.set(newMesh, key);
                }
            });

            const regionInfoData = new Map();

            const { width, height } = glraycastBuffer.data;

            if (glraycastRegionInfo.data) {
                for (const key in objOnScreenPos) {
                    if (!glraycastRegionInfo.data.has(key)) {
                        delete objOnScreenPos[key];
                    }
                }

                for (const [mesh, { x0, y, length }] of glraycastRegionInfo.data) {
                    const key = glraycastMeshKey.get(mesh);

                    if (key) {
                        let labelPos = objOnScreenPos[key];

                        if (!labelPos) {
                            labelPos = {};
                            objOnScreenPos[key] = labelPos;
                        }

                        labelPos.x = (x0+length*0.5)/width;
                        labelPos.y = y/height;
                    }
                }
            }
        });

        this._state = state;
        this._data = data;
        this._dataThree = dataThree;
        this._output = output;
        this._glraycastRegionInfo = glraycastRegionInfo;
        this._glraycastMeshTracker = glraycastMeshTracker;
        this._objOnScreenPos = objOnScreenPos;
    }

    start() {
        const {
            _state: state,
            _data: data,
            _dataThree: dataThree,
            _output: output,
            _glraycastRegionInfo: glraycastRegionInfo,
            _glraycastMeshTracker: glraycastMeshTracker,
            _objOnScreenPos: objOnScreenPos,
        } = this;

        {
            const { tour, tourLabels, session } = state;
            data.track(tour, tourLabels, session, objOnScreenPos);
        }

        {
            const { session } = state;

            const {
                objectNames,
                objectAnimations,
                slideFullObjects,
            } = data;

            const inData = {
                objectNames: objectNames.names,
                objectAnimKeys: objectAnimations.animations,
                objectCoordinates: slideFullObjects.merged,
            };

            const animations = output.assets.assets.animations.all;

            dataThree.track(session, inData, animations);
        }

        {
            const {
                assetsBase, assetsByType,
                currentSlide, slideAssets, slideCamera, slideEnvLight,
                slideFullMaterials, slideFullMaterialsByType,
                slideFullObjects,

                liveLabels, liveLabelPositions,
            } = data;

            const {
                morphTargetWeights,
                objectFinalCoords,
            } = dataThree;

            const outputData = {
                geometriesByType: assetsByType.assets.geometries,

                textures: assetsBase.assets.textures,

                envLightsByType: assetsByType.assets.envLights,

                materialsByType: assetsByType.assets.materials,
                materialGenericPropsByType: slideFullMaterialsByType.elements,
                materialTexturePropsByType: assetsByType.assets.materials,

                objectsByType: assetsByType.assets.objects,
                objectGeometriesByType: assetsByType.assets.objects,
                objectMaterialsByType: assetsByType.assets.objects,
                objectParents: assetsBase.assets.objects,
                objectTransformModifierFlags: assetsBase.assets.objects,
                objectVisibilityFlags: slideAssets.assets.objects,
                objectCoordinates: objectFinalCoords.merged,
                objectMorphTargetInfluences: morphTargetWeights.weights,

                skeletons: assetsBase.assets.skeletons,
                animations: assetsBase.assets.animations,

                activeEnvLight: slideEnvLight.envLight,
                camera: slideCamera.camera,

                liveLabels: liveLabels.labels,
                liveLabelPositions: liveLabelPositions.positions,
            };

            output.track(outputData);
        }

        glraycastRegionInfo.start();
        {
            const { data, events } = output.assets.assets.objects.all;
            glraycastMeshTracker.track(data, events);
        }

        this.update();
    }

    stop() {
        const {
            _data: data,
            _dataThree: dataThree,
            _output: output,
            _glraycastRegionInfo: glraycastRegionInfo,
            _glraycastMeshTracker: glraycastMeshTracker,
        } = this;

        data.track();
        dataThree.track();
        output.track();
        glraycastRegionInfo.stop();
        glraycastMeshTracker.track();

        this.update();
    }

    update() {
        const { _data: data, _dataThree: dataThree, _output: output } = this;

        data.update();
        dataThree.update();
        output.update();
    }
}

export default DataOutputGenerator;
